import { useMemo, useState } from 'react'
import { Check, MapPin, Phone, Search } from 'lucide-react'
import type { NtBlock, NtCheckin, NtDay } from '../../core/types'
import { useAuth } from '../../core/auth'
import { useNorseTerm } from './NorseTermData'
import { useApprovedProposals, useCheckins, addCheckin, DAY_LABELS } from './model'
import { termDay } from './attendanceLogic'
import { projectGroups, blockPlan, hostsOn, blockNow, checkinsFor, matchesSearch, ENGAGEMENT, PROGRESS, type ProjectGroup } from './checkinLogic'
import { Loading, Empty, ErrorState } from '../../ui/States'
import { Area } from '../../ui/Field'

/**
 * Project check-ins: a staff member, in the block they are not teaching,
 * finds an off-site independent project (a joint project shows as one), sees
 * what its students' plan says for that block and where, and records a visit:
 * engagement, progress against the plan, whether they were where they said,
 * and notes. Staff see every check-in; students never do. Each visit is a
 * check mark on the project. Records of visits: never edited or deleted.
 */
const BLOCK_NAME = (b: NtBlock) => (b === 'AM' ? 'Morning' : 'Afternoon')

export function Checkins() {
  const { profile, user } = useAuth()
  const { term, mySessions } = useNorseTerm()
  const proposals = useApprovedProposals(term.id, true)
  const checkins = useCheckins(term.id, true)
  const today = termDay(term)
  const [day, setDay] = useState<NtDay>(() => (today !== null && today <= 2 ? today : 0) as NtDay)
  // The block they are free: the one they are not teaching that day, or now.
  const teaching = new Set(mySessions.map((s) => s.block))
  const free: NtBlock = teaching.has('AM') && !teaching.has('PM') ? 'PM' : teaching.has('PM') && !teaching.has('AM') ? 'AM' : blockNow()
  const [block, setBlock] = useState<NtBlock>(free)
  const [q, setQ] = useState('')
  const groups = useMemo(() => projectGroups(proposals ?? []), [proposals])
  if (proposals === null || checkins === null) return <Loading label="Loading projects" />

  const me = { email: (user?.email ?? '').toLowerCase(), name: profile?.displayName ?? '' }
  const out = (g: ProjectGroup) => g.proposals.some((p) => blockPlan(p, day, block).length > 0)
  const shown = groups.filter((g) => matchesSearch(g, q)).sort((a, b) => Number(out(b)) - Number(out(a)) || a.names.localeCompare(b.names))

  return (
    <>
      <p className="ov__p" style={{ marginTop: 'var(--s-2)' }}>
        Visit an off-site project in a block you are not teaching. Find the student, see what their plan says and where,
        then check in. Staff see every check-in; students never do.
      </p>
      <div className="fpd__filters">
        <div className="chips" role="tablist" aria-label="Day">
          {([0, 1, 2] as NtDay[]).map((d) => (
            <button key={d} role="tab" aria-selected={day === d} className={`chip${day === d ? ' is-on' : ''}`} onClick={() => setDay(d)}>
              {DAY_LABELS[d]}{today === d ? ' (today)' : ''}
            </button>
          ))}
        </div>
        <div className="chips" role="tablist" aria-label="Block">
          {(['AM', 'PM'] as NtBlock[]).map((b) => (
            <button key={b} role="tab" aria-selected={block === b} className={`chip${block === b ? ' is-on' : ''}`} onClick={() => setBlock(b)}>
              {BLOCK_NAME(b)}{teaching.size === 1 && b === free ? ' (you are free)' : ''}
            </button>
          ))}
        </div>
        <label className="sel__search"><Search size={15} aria-hidden="true" />
          <input className="opt__input" placeholder="Find a student, project or place" aria-label="Find a project" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
      </div>

      {groups.length === 0 ? (
        <Empty title="No approved projects yet" body="Independent projects show here once they are approved." />
      ) : shown.length === 0 ? <p className="muted-note">Nothing matches.</p> : (
        <ul className="fpd__posts">
          {shown.map((g) => <ProjectCard key={g.key} g={g} day={day} block={block} checkins={checkinsFor(checkins, g.key)} me={me} termId={term.id} />)}
        </ul>
      )}
    </>
  )
}

function ProjectCard({ g, day, block, checkins, me, termId }: {
  g: ProjectGroup; day: NtDay; block: NtBlock; checkins: NtCheckin[]; me: { email: string; name: string }; termId: string
}) {
  const [checking, setChecking] = useState(false)
  const [showAll, setShowAll] = useState(false)
  return (
    <li className="card fpd__post">
      <div className="fpd__posthead">
        <div>
          <div className="nextup__eyebrow">{g.proposals.length > 1 ? 'Joint project' : 'Independent project'}</div>
          <h3 className="fpd__posttitle">{g.names}</h3>
          <div className="work__sub">{g.title}</div>
        </div>
        <span className={`pill${checkins.length ? ' pill--good' : ''}`} title={checkins.map((c) => c.visitorName).join(', ')}>
          {checkins.length ? <><Check size={12} aria-hidden="true" /> {checkins.length} {checkins.length === 1 ? 'check-in' : 'check-ins'}</> : 'No check-ins yet'}
        </span>
      </div>

      <div className="ck__where">
        <div className="opt__label" style={{ margin: 0 }}>{DAY_LABELS[day]} {BLOCK_NAME(block).toLowerCase()}</div>
        {g.proposals.map((p) => {
          const plan = blockPlan(p, day, block)
          const hosts = hostsOn(p, day)
          return (
            <div key={p.id} className="ck__student">
              {g.proposals.length > 1 && <strong>{p.studentName}</strong>}
              {plan.length === 0 ? <div className="work__sub">Nothing planned for this block.</div> : (
                <ul className="ck__plan">
                  {plan.map((b) => <li key={b.part}>{b.what || 'Not described'}{b.where ? <span className="work__sub"> · {b.where}</span> : null}</li>)}
                </ul>
              )}
              {hosts.map((h, i) => (
                <div key={i} className="ck__host">
                  <MapPin size={14} aria-hidden="true" />
                  <span><strong>{h.org || 'Host'}</strong>{h.address ? `, ${h.address}` : ''}
                    {h.name ? <span className="work__sub"> · {h.name}{h.role ? `, ${h.role}` : ''}</span> : null}
                    {h.phone ? <> · <a href={`tel:${h.phone}`}><Phone size={12} aria-hidden="true" /> {h.phone}</a></> : null}</span>
                </div>
              ))}
            </div>
          )
        })}
      </div>

      {checking
        ? <CheckinForm g={g} day={day} block={block} me={me} termId={termId} onDone={() => setChecking(false)} />
        : <div className="fpd__postacts"><button className="btn" style={{ width: 'auto' }} onClick={() => setChecking(true)}>Check in</button></div>}

      {checkins.length > 0 && (
        <ul className="phist" style={{ marginTop: 'var(--s-3)' }}>
          {(showAll ? checkins : checkins.slice(0, 2)).map((c) => (
            <li key={c.id}>
              <strong>{c.visitorName}</strong>
              <span className="phist__when"> · {DAY_LABELS[c.day]} {BLOCK_NAME(c.block).toLowerCase()}
                {(c.at as { toDate?: () => Date })?.toDate ? ` · ${(c.at as { toDate: () => Date }).toDate().toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}</span>
              <div className="work__sub">
                Engagement: {ENGAGEMENT.find((e) => e.k === c.engagement)?.label} · {PROGRESS.find((x) => x.k === c.progress)?.label} · {c.whereExpected ? 'Where the plan said' : 'Not where the plan said'}
              </div>
              {c.note && <div className="phist__note">{c.note}</div>}
            </li>
          ))}
          {checkins.length > 2 && <li><button className="linkish" onClick={() => setShowAll((x) => !x)}>{showAll ? 'Show fewer' : `Show all ${checkins.length}`}</button></li>}
        </ul>
      )}
    </li>
  )
}

function CheckinForm({ g, day, block, me, termId, onDone }: {
  g: ProjectGroup; day: NtDay; block: NtBlock; me: { email: string; name: string }; termId: string; onDone: () => void
}) {
  const [engagement, setEngagement] = useState<NtCheckin['engagement'] | null>(null)
  const [progress, setProgress] = useState<NtCheckin['progress'] | null>(null)
  const [where, setWhere] = useState<boolean | null>(null)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const ok = engagement && progress && where !== null
  const save = async () => {
    if (!ok) return
    setBusy(true); setErr(null)
    try {
      await addCheckin({ termId, groupKey: g.key, studentEmails: g.emails, proposalIds: g.proposals.map((p) => p.id), day, block,
        visitorEmail: me.email, visitorName: me.name, engagement, progress, whereExpected: where, note })
      onDone()
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)) } finally { setBusy(false) }
  }
  const chip = <T,>(on: boolean, label: string, onClick: () => void, key: T) => (
    <button key={String(key)} type="button" aria-pressed={on} className={`chip${on ? ' is-on' : ''}`} onClick={onClick}>{label}</button>
  )
  return (
    <div className="host__card" style={{ marginTop: 'var(--s-3)' }}>
      {err && <ErrorState body={`Not saved. ${err}`} />}
      <div className="opt__label" style={{ margin: 0 }}>Engagement <span className="req">*</span></div>
      <div className="chips" style={{ margin: '4px 0 var(--s-3)' }}>{ENGAGEMENT.map((e) => chip(engagement === e.k, e.label, () => setEngagement(e.k), e.k))}</div>
      <div className="opt__label" style={{ margin: 0 }}>Progress against their plan <span className="req">*</span></div>
      <div className="chips" style={{ margin: '4px 0 var(--s-3)' }}>{PROGRESS.map((x) => chip(progress === x.k, x.label, () => setProgress(x.k), x.k))}</div>
      <div className="opt__label" style={{ margin: 0 }}>Were they where the plan said? <span className="req">*</span></div>
      <div className="chips" style={{ margin: '4px 0 var(--s-3)' }}>
        {chip(where === true, 'Yes', () => setWhere(true), 'y')}{chip(where === false, 'No', () => setWhere(false), 'n')}
      </div>
      <Area label="Notes" value={note} onChange={setNote} placeholder="What they were doing, what stood out, anything the office or their seminar teacher should know." />
      <p className="opt__hint" style={{ marginTop: '-8px' }}>Staff can read this; students cannot. It can’t be edited after you save it.</p>
      <div style={{ display: 'flex', gap: 'var(--s-2)' }}>
        <button className="btn" style={{ width: 'auto' }} disabled={busy || !ok} onClick={save}>{busy ? 'Saving…' : 'Save check-in'}</button>
        <button className="btn btn--ghost" style={{ width: 'auto' }} onClick={onDone}>Cancel</button>
      </div>
    </div>
  )
}
